import { useCallback, useEffect, useState } from "react";
import { Award, Download, Send, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";

async function downloadCert(c) {
  const { data } = await api.get(`/certificates/${c.id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${c.loan_number}-paid-in-full-certificate.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const Row = ({ c, admin, onChanged }) => {
  const [busy, setBusy] = useState("");
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "dl") await downloadCert(c);
      else {
        const { data } = await api.post(`/certificates/${c.id}/resend`);
        (data.status === "Sent" ? toast.success : toast.error)(data.message);
        onChanged();
      }
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusy("");
    }
  };
  const revoked = c.status !== "issued";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={`certificate-row-${c.id}`}>
      <div className="flex items-center gap-3">
        <Award className={`h-6 w-6 ${revoked ? "text-muted-foreground" : "text-[hsl(var(--gold))]"}`} />
        <div>
          <div className="font-semibold">Paid-in-full certificate</div>
          <div className="text-xs text-muted-foreground">
            Paid in full {fmtDate(c.figures.paid_in_full_date)}
            {c.figures.overpayment_cents > 0 && ` · overpayment ${money(c.figures.overpayment_cents)} to refund`}
            {admin && <> · <span data-testid={`certificate-status-${c.id}`} className={revoked ? "font-semibold text-destructive" : ""}>{c.status_label}</span>
              {c.email_status && ` · email ${c.email_status}`}{c.email_error && <span className="text-destructive"> ({c.email_error})</span>}</>}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => run("dl")} disabled={!!busy} data-testid={`certificate-download-${c.id}`}>
          {busy === "dl" ? <Loader2 className="animate-spin" /> : <Download />} Download
        </Button>
        {admin && !revoked && (
          <Button variant="outline" size="sm" onClick={() => run("send")} disabled={!!busy} data-testid={`certificate-resend-${c.id}`}>
            {busy === "send" ? <Loader2 className="animate-spin" /> : <Send />} Resend certificate
          </Button>
        )}
      </div>
    </div>
  );
};

export const CertificatePanel = ({ loanId, admin, refreshKey }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/loans/${loanId}/certificates`).then((r) => setD(r.data)).catch(() => setD(null)), [loanId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!d || (!d.items.length && !d.alert)) return null;
  return (
    <div className="mb-6 space-y-3" data-testid="certificate-panel">
      {d.alert && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive" data-testid="certificate-revoked-alert">
          <AlertTriangle className="h-4 w-4" /> {d.alert}
        </div>
      )}
      {d.items.length > 0 && (
        <div className="divide-y rounded-lg border bg-card">{d.items.map((c) => <Row key={c.id} c={c} admin={admin} onChanged={load} />)}</div>
      )}
    </div>
  );
};
